// Daily cycles tab: one card per cycle, newest first, with every record and signature
// of that cycle. Display only; work eligibility is shown by the server at the top of
// the permit page.
import type { ReactNode } from 'react'
import { useState } from 'react'
import { RuleRefs } from '@/components/evidence'
import { Badge, Banner, Button, Card, EmptyState, KeyValue } from '@/components/ui'
import {
  AA_VALIDATION_CHECKS,
  ACCEPTANCE_DECLARATIONS,
  GAS_TEST_KINDS,
  PREISSUE_CHECKS,
  SUSPENSION_REASONS,
} from '@/domain/catalogue'
import type { CycleView, GasTestView, PermitBundle, Registry, SuspensionView, ToolboxTalkView } from '@/domain/types'
import type { PermitViewProps } from '@/features/contracts'
import { fmtDateTime, hoursText, labelFor, retestHours } from '@/lib/format'
import { useRegistry } from '@/lib/queries'
import { TalkContent } from './briefing'
import {
  findGasTest,
  gasPlanOf,
  type GasPlan,
  latestTest,
  predecessorOf,
  requiresFirstDayExam,
  revisionOf,
  sortCyclesNewestFirst,
  speciesLabel,
  suspensionConfirmations,
  talkSignatures,
  talksNewestFirst,
  testsNewestFirst,
  textOf,
} from './logic'
import { CycleSignature, CycleStateBadge, GasClassificationBadge, GasClassificationText, Mark, roleLabel, SignedState, WindowText } from './parts'

type People = Registry['people']

const EXAM_KIND_TEXT: Record<string, string> = {
  // The exam kinds keep their historic aa_ codes; the RS examines from 0016, and the
  // signature line below shows who signed in which role.
  aa_first_day: 'First-day worksite examination at validation',
  aa_daily: 'Daily worksite examination at validation',
  aa_revalidation: 'Worksite examination at revalidation',
  pa_rs_pre_authorisation: 'PA/RS visit before authorisation',
}

const END_REASON_TEXT: Record<string, string> = {
  validation_period_elapsed: 'the validation period ended',
  request_window_elapsed: 'the requested window ended without validation',
  ph_cancellation: 'the PH cancelled the permit',
  suspended: 'work was suspended',
  replaced: 'a new cycle replaced it',
}

const CAPACITY_TEXT: Record<string, string> = {
  PH: 'Permit Holder (formal stop)',
  RS: 'Responsible Supervisor',
  AA: 'Area Authority (historic)',
  PA: 'Permit Applicant',
  AGT: 'Authorised Gas Tester',
  fact_reporter: 'Fact report by someone on site (not a licensed signature)',
  system_gas_result: 'Automatic: unsafe gas test result',
}

const GAS_CONDITION: Record<string, { label: string; tone: 'ok' | 'warn' | 'neutral' }> = {
  complete: { label: 'Complete at validation', tone: 'ok' },
  prearranged: { label: 'Pre-arranged (not done yet)', tone: 'warn' },
  not_required: { label: 'Not required', tone: 'neutral' },
}

function humanise(code: string | null | undefined): string {
  return code ? code.replace(/_/g, ' ') : '-'
}

function Section({ title, ruleIds, decisionIds, aside, children }: {
  title: ReactNode
  ruleIds?: string[]
  decisionIds?: string[]
  aside?: ReactNode
  children: ReactNode
}) {
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-line pb-1">
        <h4 className="display text-base font-semibold text-ink">
          {title}
          <RuleRefs ruleIds={ruleIds} decisionIds={decisionIds} />
        </h4>
        {aside}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  )
}

function SignedBadge({ signed }: { signed: boolean }) {
  return signed ? (
    <Badge tone="ok">
      <span aria-hidden>&#10003;</span>Signed
    </Badge>
  ) : (
    <Badge>Not yet</Badge>
  )
}

function Rows({ children }: { children: ReactNode }) {
  return <ul className="divide-y divide-line rounded-lg border border-line bg-white">{children}</ul>
}

/** Label and value on one line when they fit; a long value drops below the label on phones. */
function Row({ label, value, note }: { label: ReactNode; value: ReactNode; note?: ReactNode }) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 p-2 text-sm">
      <span className="min-w-0 flex-[1_1_12rem] text-ink">{label}</span>
      <span className="shrink-0">{value}</span>
      {note ? <span className="w-full text-xs text-ink-soft">{note}</span> : null}
    </li>
  )
}

/** Codes recorded on a check list that the catalogue does not know: shown, never hidden. */
function extraEntries(record: Record<string, boolean>, known: readonly { code: string }[]): [string, boolean][] {
  return Object.entries(record).filter(([code]) => !known.some((k) => k.code === code))
}

// -----------------------------------------------------------------------------
// Progress strip (recorded facts only)
// -----------------------------------------------------------------------------
function CycleProgress({ cycle, gasPlan }: { cycle: CycleView; gasPlan: GasPlan | null }) {
  const latest = latestTest(cycle)
  const talk = cycle.toolbox_talks.find((t) => t.complete) ?? talksNewestFirst(cycle.toolbox_talks)[0] ?? null
  const signed = talk ? talkSignatures(talk) : null
  const items: { label: string; body: ReactNode }[] = [
    {
      label: 'RS pre-issue',
      body: cycle.preissue ? <Mark ok yes={`Signed by ${cycle.preissue.attestation.signer.display_name}`} /> : <span className="text-ink-soft">Not yet</span>,
    },
    {
      label: 'RS validation',
      body: cycle.validation ? <Mark ok yes={`Signed by ${cycle.validation.attestation.signer.display_name}`} /> : <span className="text-ink-soft">Not yet</span>,
    },
    {
      label: 'Gas test',
      body: !gasPlan ? (
        <span className="text-ink-soft">Not required by the permit</span>
      ) : latest ? (
        <>
          <GasClassificationText test={latest} />
          <span className="mt-0.5 block text-ink-soft">Latest, observed {fmtDateTime(latest.reported_observed_at)}</span>
        </>
      ) : (
        <span className="text-ink-soft">None recorded</span>
      ),
    },
    {
      label: 'Toolbox talk',
      body: !talk ? (
        <span className="text-ink-soft">Not yet</span>
      ) : talk.complete ? (
        <Mark ok yes={`All ${signed?.total ?? 0} attendees signed`} />
      ) : (
        <span className="font-semibold text-warn">
          {signed?.signed ?? 0} of {signed?.total ?? 0} attendees signed
        </span>
      ),
    },
    {
      label: 'PH acceptance',
      body: cycle.acceptance ? <Mark ok yes={`Signed by ${cycle.acceptance.attestation.signer.display_name}`} /> : <span className="text-ink-soft">Not yet</span>,
    },
  ]
  return (
    <ol className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5" aria-label={`Cycle ${cycle.cycle_number} progress`}>
      {items.map((i) => (
        <li key={i.label} className="rounded-lg border border-line bg-white p-2 text-xs">
          <p className="font-semibold uppercase tracking-wide text-ink-faint">{i.label}</p>
          <div className="mt-1">{i.body}</div>
        </li>
      ))}
    </ol>
  )
}

function StopAlert({ cycle, isCurrent }: { cycle: CycleView; isCurrent: boolean }) {
  if (cycle.suspensions.length === 0) return null
  const reasons = [...new Set(cycle.suspensions.map((s) => labelFor(SUSPENSION_REASONS, s.reason_code)))].join('; ')
  if (isCurrent && cycle.cycle_state === 'suspension_reported') {
    return (
      <Banner tone="bad" title="Stop reported on this cycle: work must stop" className="mt-3">
        {reasons}. Restart needs PH and RS confirmation, then a new cycle.
      </Banner>
    )
  }
  return (
    <Banner tone={isCurrent ? 'warn' : 'neutral'} title={`This cycle had a stop: ${reasons}`} className="mt-3">
      The stop record stays on the permit. Work restarts only through a new cycle.
    </Banner>
  )
}

// -----------------------------------------------------------------------------
// Sections
// -----------------------------------------------------------------------------
function PreissueSection({ cycle }: { cycle: CycleView }) {
  const p = cycle.preissue
  return (
    <Section title="RS pre-issue" ruleIds={['CORE-052']} aside={<SignedBadge signed={Boolean(p)} />}>
      {p ? (
        <>
          <Rows>
            {PREISSUE_CHECKS.map((o) => (
              <Row key={o.code} label={o.label} value={<Mark ok={p.checks[o.code] === true} />} />
            ))}
            {extraEntries(p.checks, PREISSUE_CHECKS).map(([code, v]) => (
              <Row key={code} label={<span className="code">{code}</span>} value={<Mark ok={v} />} />
            ))}
          </Rows>
          <KeyValue
            items={[
              ['Frozen plan or MoC reference', p.plan_reference],
              ['Conflict and SIMOPS review reference', p.conflict_review_reference],
            ]}
          />
          <CycleSignature a={p.attestation} what="RS pre-issue" cycleNumber={cycle.cycle_number} />
        </>
      ) : (
        <p className="text-sm text-ink-soft">The RS has not pre-issued this cycle.</p>
      )}
    </Section>
  )
}

function CheckResult({ result, code }: { result: string | null; code?: string }) {
  switch (result) {
    case 'confirmed':
      // The RS validation panel offers the gas check as Complete or Pre-arranged.
      return <Mark ok yes={code === 'gas_tests' ? 'Complete' : 'Confirmed'} />
    case 'not_applicable':
      return <span className="whitespace-nowrap font-semibold text-ink-soft">&ndash; Not applicable</span>
    case 'prearranged':
      return (
        <span className="whitespace-nowrap font-semibold text-warn">
          <span aria-hidden>&#9719; </span>Pre-arranged
        </span>
      )
    case null:
      return <Mark ok={false} no="Not recorded" />
    default:
      return <span className="font-semibold text-ink">{humanise(result)}</span>
  }
}

function Prearrangement({ data, people }: { data: Record<string, unknown>; people: People }) {
  const known = new Set(['tester_person_id', 'tester', 'tester_display_name', 'planned_at', 'sample_location', 'species'])
  const testerObj = data.tester && typeof data.tester === 'object' ? (data.tester as Record<string, unknown>) : null
  const testerId = textOf(data.tester_person_id) || textOf(testerObj?.id)
  const testerName =
    textOf(testerObj?.display_name) || textOf(data.tester_display_name) || people.find((p) => p.id === testerId)?.display_name || testerId || '-'
  const species = Array.isArray(data.species) ? data.species.filter((s): s is string => typeof s === 'string') : []
  const others = Object.entries(data).filter(([k]) => !known.has(k))
  return (
    <div className="rounded-lg border border-warn/30 bg-warn-light p-3">
      <p className="mb-2 text-sm font-semibold text-ink">Pre-arranged gas test</p>
      <KeyValue
        items={[
          ['Tester (AGT)', testerName],
          ['Planned for', fmtDateTime(textOf(data.planned_at) || null)],
          ['Sample location', textOf(data.sample_location) || '-'],
          ['Gases', species.length ? species.map(speciesLabel).join(', ') : '-'],
          ...others.map(([k, v]): [ReactNode, ReactNode] => [humanise(k), typeof v === 'string' ? v : JSON.stringify(v)]),
        ]}
      />
    </div>
  )
}

function StopResolution({ data, bundle }: { data: Record<string, unknown>; bundle: PermitBundle }) {
  const testId = textOf(data.resolving_gas_test_id)
  const found = findGasTest(bundle, testId)
  const others = Object.entries(data).filter(([k]) => k !== 'note' && k !== 'resolving_gas_test_id')
  return (
    <div className="rounded-lg border border-line bg-white p-3">
      <p className="mb-2 text-sm font-semibold text-ink">How the previous stop was resolved</p>
      <KeyValue
        cols={1}
        items={[
          ['Validator note', textOf(data.note) || '-'],
          [
            'Gas test cited',
            !testId ? (
              'None'
            ) : found ? (
              <span className="flex flex-wrap items-center gap-2">
                Cycle {found.cycle.cycle_number}, observed {fmtDateTime(found.test.reported_observed_at)}
                <GasClassificationBadge test={found.test} />
              </span>
            ) : (
              <span className="code">{testId}</span>
            ),
          ],
          ...others.map(([k, v]): [ReactNode, ReactNode] => [humanise(k), typeof v === 'string' ? v : JSON.stringify(v)]),
        ]}
      />
    </div>
  )
}

function ValidationSection({ cycle, bundle, people }: { cycle: CycleView; bundle: PermitBundle; people: People }) {
  const v = cycle.validation
  const byCode = new Map((v?.checks ?? []).map((c) => [c.check_code, c]))
  const extra = (v?.checks ?? []).filter((c) => !AA_VALIDATION_CHECKS.some((o) => o.code === c.check_code))
  const gas = v ? (GAS_CONDITION[v.gas_condition] ?? { label: humanise(v.gas_condition), tone: 'neutral' as const }) : null
  return (
    <Section title="RS validation" ruleIds={['CORE-055', 'CORE-056']} aside={<SignedBadge signed={Boolean(v)} />}>
      {v && gas ? (
        <>
          <KeyValue
            items={[
              ['Validation period', <WindowText from={v.validation_from} to={v.validation_to} />],
              ['Gas condition', <Badge tone={gas.tone}>{gas.label}</Badge>],
            ]}
          />
          {v.gas_condition === 'prearranged' ? (
            <Banner tone="warn" title="Pre-arranged is not a gas test">
              Work cannot start until the AGT records an acceptable test in this cycle.
            </Banner>
          ) : null}
          {v.prearrangement ? <Prearrangement data={v.prearrangement} people={people} /> : null}
          {v.stop_resolution ? <StopResolution data={v.stop_resolution} bundle={bundle} /> : null}
          <p className="text-sm font-semibold text-ink">The sixteen validation checks</p>
          <Rows>
            {AA_VALIDATION_CHECKS.map((o, i) => {
              const c = byCode.get(o.code)
              return (
                <Row
                  key={o.code}
                  label={
                    <>
                      <span className="text-ink-faint">{i + 1}. </span>
                      {o.label}
                    </>
                  }
                  value={<CheckResult result={c?.result ?? null} code={o.code} />}
                  note={c?.note ? `Note: ${c.note}` : undefined}
                />
              )
            })}
            {extra.map((c) => (
              <Row key={c.check_code} label={<span className="code">{c.check_code}</span>} value={<CheckResult result={c.result} />} note={c.note ?? undefined} />
            ))}
          </Rows>
          <CycleSignature a={v.attestation} what="Cycle validation" cycleNumber={cycle.cycle_number} />
        </>
      ) : (
        <p className="text-sm text-ink-soft">The RS has not validated this cycle.</p>
      )}
    </Section>
  )
}

function ExaminationsSection({ cycle, required }: { cycle: CycleView; required: boolean }) {
  if (cycle.examinations.length === 0 && !required) return null
  return (
    <Section title="Worksite examinations" ruleIds={['CORE-035']}>
      {required && cycle.examinations.length === 0 ? (
        <p className="text-sm text-ink-soft">The permit requires a worksite examination for this cycle. The RS records it with the cycle validation.</p>
      ) : null}
      {cycle.examinations.map((x) => (
        <div key={x.id} className="rounded-lg border border-line bg-white p-3">
          <p className="font-semibold text-ink">{EXAM_KIND_TEXT[x.exam_kind] ?? humanise(x.exam_kind)}</p>
          <KeyValue
            cols={1}
            items={[
              ['Site examined at (reported)', fmtDateTime(x.reported_observed_at)],
              ['Findings', x.findings],
            ]}
          />
          <div className="mt-2">
            <CycleSignature a={x.attestation} what="Worksite examination" cycleNumber={cycle.cycle_number} />
          </div>
        </div>
      ))}
    </Section>
  )
}

/** Class A (0030): the validating RS's declaration and who did the first gas test of the cycle. */
function ClassASection({ cycle, bundle }: { cycle: CycleView; bundle: PermitBundle }) {
  const c = bundle.class_a?.validation_conditions.find((x) => x.cycle_id === cycle.id)
  if (!bundle.class_a || !c) return null
  return (
    <Section title="Class A conditions" ruleIds={['CORE-039', 'CORE-055']}>
      <div className="rounded-lg border border-class-a bg-white p-3">
        <KeyValue
          cols={1}
          items={[
            ['Validating RS stays in the work area', c.rs_remains_in_area ? 'Declared' : 'Not declared'],
            ['Worksite examination at this validation', c.daily_examination_required ? 'Required (Class A)' : 'Not required by the rules'],
            [
              'First gas test of the cycle',
              c.first_gas_test_source === 'not_required'
                ? 'No gas test required'
                : `${c.first_gas_tester?.display_name ?? 'Unknown'}${c.first_gas_test_source === 'prearranged' ? ' (pre-arranged)' : ''}${c.first_gas_test_delegated ? ', delegated by the validating RS' : ', the validating RS'}`,
            ],
            ...(c.first_gas_test_delegated ? ([['Delegation', c.delegation_note ?? '']] as [string, string][]) : []),
          ]}
        />
        <div className="mt-2">
          <CycleSignature a={c.attestation} what="Class A conditions" cycleNumber={cycle.cycle_number} />
        </div>
      </div>
    </Section>
  )
}

function Declaration({ m }: { m: GasTestView['measurements'][number] }) {
  if (m.agt_declared_within_criteria === true) return <Mark ok yes="Within criteria (declared)" />
  if (m.agt_declared_within_criteria === false) return <Mark ok={false} no="Outside criteria (declared)" />
  return <span className="font-semibold text-warn">Not declared ({humanise(m.comparison_result)})</span>
}

function GasTestBlock({ test, cycleNumber }: { test: GasTestView; cycleNumber: number }) {
  return (
    <div className="rounded-lg border border-line bg-white p-3">
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-ink">{labelFor(GAS_TEST_KINDS, test.test_kind)}</p>
          <p className="text-sm text-ink-soft">Sample location: {test.sample_location}</p>
        </div>
        <GasClassificationBadge test={test} />
      </div>
      <KeyValue
        items={[
          ['When the readings were taken (reported by the AGT)', fmtDateTime(test.reported_observed_at)],
          ['Recorded (server clock)', fmtDateTime(test.recorded_at)],
          ['Instrument', test.instrument ? `${test.instrument.instrument_identifier} - ${test.instrument.model}` : 'Not recorded'],
          ['Method reference', test.method_reference ?? 'None'],
        ]}
      />
      <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-ink-faint">Readings</p>
      <Rows>
        {test.measurements.map((m) => (
          <Row
            key={m.species_code}
            label={speciesLabel(m.species_code)}
            value={
              <span className="code font-semibold text-ink">
                {m.measured_value} {m.unit_code}
              </span>
            }
            note={<Declaration m={m} />}
          />
        ))}
      </Rows>
      {test.notes ? <p className="mt-2 text-sm text-ink">Notes: {test.notes}</p> : null}
      <div className="mt-2">
        <CycleSignature a={test.attestation} what="AGT gas test" cycleNumber={cycleNumber} />
      </div>
    </div>
  )
}

function GasSection({ cycle, gasPlan, synthetic }: { cycle: CycleView; gasPlan: GasPlan | null; synthetic: boolean }) {
  const tests = testsNewestFirst(cycle.gas_tests)
  return (
    <Section title="Gas tests" ruleIds={['CORE-038', 'CORE-040', 'CORE-041', 'CORE-044']} decisionIds={['DEC-CORE-005']}>
      <Banner tone="synthetic" title="No approved gas limits">
        {synthetic
          ? 'No approved gas limits exist yet (DEC-CORE-005). In this synthetic demo the tester declares each reading against a placeholder method.'
          : 'No approved gas limits exist yet (DEC-CORE-005). Each result shows the basis it was classified on.'}
      </Banner>
      <p className="text-sm text-ink">
        {gasPlan ? (
          <>
            Gas plan: test for {gasPlan.species.map(speciesLabel).join(', ') || 'no gases listed'}
            {retestHours(gasPlan) !== null ? `; retest every ${hoursText(retestHours(gasPlan) ?? 0)}` : ''}
            {gasPlan.continuous_monitoring ? '; continuous monitoring during work' : ''}.
          </>
        ) : (
          'The permit has no gas testing requirement.'
        )}
      </p>
      {tests.length === 0 ? (
        <p className="text-sm text-ink-soft">No gas test recorded in this cycle.</p>
      ) : (
        tests.map((t) => <GasTestBlock key={t.id} test={t} cycleNumber={cycle.cycle_number} />)
      )}
    </Section>
  )
}

function TalkBlock({ talk, cycleNumber }: { talk: ToolboxTalkView; cycleNumber: number }) {
  const s = talkSignatures(talk)
  return (
    <div className="space-y-3 rounded-lg border border-line bg-white p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="font-semibold text-ink">
          Given by {talk.giver.signer.display_name} ({roleLabel(talk.giver_role)}), {fmtDateTime(talk.reported_conducted_at)}
        </p>
        {talk.complete ? (
          <Badge tone="ok">
            <span aria-hidden>&#10003;</span>All {s.total} attendees signed
          </Badge>
        ) : (
          <Badge tone="warn">
            {s.signed} of {s.total} attendees signed
          </Badge>
        )}
      </div>
      <TalkContent talk={talk} />
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Attendees (each signs on their own login)</p>
        {talk.attendees.length === 0 ? (
          <p className="text-sm text-ink-soft">No attendees recorded.</p>
        ) : (
          <Rows>
            {talk.attendees.map((a) => (
              <Row
                key={a.id}
                label={
                  <>
                    {a.person.display_name}
                    {a.person.organisation ? <span className="text-ink-soft"> ({a.person.organisation})</span> : null}
                    {a.role_in_crew ? <span className="text-ink-soft"> - {a.role_in_crew}</span> : null}
                  </>
                }
                value={<SignedState a={a.signed} />}
              />
            ))}
          </Rows>
        )}
      </div>
      <CycleSignature a={talk.giver} what={`Toolbox talk given by the ${talk.giver_role}`} cycleNumber={cycleNumber} />
    </div>
  )
}

function TalksSection({ cycle }: { cycle: CycleView }) {
  return (
    <Section title="Toolbox talk" ruleIds={['CORE-060', 'CORE-061']}>
      {cycle.toolbox_talks.length === 0 ? (
        <p className="text-sm text-ink-soft">No toolbox talk recorded for this cycle.</p>
      ) : (
        talksNewestFirst(cycle.toolbox_talks).map((t) => <TalkBlock key={t.id} talk={t} cycleNumber={cycle.cycle_number} />)
      )}
    </Section>
  )
}

function AcceptanceSection({ cycle }: { cycle: CycleView }) {
  const acc = cycle.acceptance
  const talk = acc ? cycle.toolbox_talks.find((t) => t.id === acc.toolbox_talk_id) : null
  return (
    <Section title="PH acceptance" ruleIds={['CORE-057', 'CORE-058']} aside={<SignedBadge signed={Boolean(acc)} />}>
      {acc ? (
        <>
          <Rows>
            {ACCEPTANCE_DECLARATIONS.map((o) => (
              <Row key={o.code} label={o.label} value={<Mark ok={acc.declarations[o.code] === true} yes="Declared" no="Not declared" />} />
            ))}
            {extraEntries(acc.declarations, ACCEPTANCE_DECLARATIONS).map(([code, v]) => (
              <Row key={code} label={<span className="code">{code}</span>} value={<Mark ok={v} />} />
            ))}
          </Rows>
          <p className="text-sm text-ink">
            Toolbox talk used:{' '}
            {talk ? (
              `given by ${talk.giver.signer.display_name}, ${fmtDateTime(talk.reported_conducted_at)}`
            ) : (
              <span className="code">{acc.toolbox_talk_id}</span>
            )}
          </p>
          <CycleSignature a={acc.attestation} what="PH acceptance" cycleNumber={cycle.cycle_number} />
        </>
      ) : (
        <p className="text-sm text-ink-soft">The PH has not accepted this cycle.</p>
      )}
    </Section>
  )
}

function StopBlock({ stop, cycle, bundle }: { stop: SuspensionView; cycle: CycleView; bundle: PermitBundle }) {
  const { phDeclared, rsConfirmed, rsConfirmedAs } = suspensionConfirmations(stop)
  const supervisor = rsConfirmedAs === 'AA' ? roleLabel('AA') : 'RS'
  const source = findGasTest(bundle, stop.source_gas_test_id)
  return (
    <div className="space-y-2 rounded-lg border border-bad/30 bg-white p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="font-semibold text-ink">{labelFor(SUSPENSION_REASONS, stop.reason_code)}</p>
        {phDeclared && rsConfirmed ? <Badge tone="neutral">Confirmed by PH and {supervisor}</Badge> : <Badge tone="bad">Waiting for confirmation</Badge>}
      </div>
      <p className="text-sm text-ink">{stop.reported_condition}</p>
      <KeyValue
        items={[
          ['Reported by', stop.reporter ? stop.reporter.display_name : 'The system'],
          ['As', CAPACITY_TEXT[stop.reporter_capacity] ?? humanise(stop.reporter_capacity)],
          ['Observed (reported)', fmtDateTime(stop.reported_observed_at)],
          ['Received (server clock)', fmtDateTime(stop.server_received_at)],
        ]}
      />
      {stop.source_gas_test_id ? (
        <p className="flex flex-wrap items-center gap-2 text-sm text-ink">
          Raised by the gas test observed {source ? fmtDateTime(source.test.reported_observed_at) : <span className="code">{stop.source_gas_test_id}</span>}
          {source ? <GasClassificationBadge test={source.test} /> : null}
        </p>
      ) : null}
      {stop.ph_attestation ? <CycleSignature a={stop.ph_attestation} what="PH stop declaration" cycleNumber={cycle.cycle_number} /> : null}
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Confirmations</p>
      {stop.confirmations.length === 0 ? <p className="text-sm text-ink-soft">None yet.</p> : null}
      {stop.confirmations.map((c) => (
        <div key={c.attestation.id} className="space-y-1">
          <p className="text-sm text-ink">
            {roleLabel(c.confirmation_role)}: safe state confirmed <Mark ok={c.safe_state_confirmed} />
          </p>
          {c.notes ? <p className="text-sm text-ink-soft">Notes: {c.notes}</p> : null}
          <CycleSignature a={c.attestation} what={`${roleLabel(c.confirmation_role)} stop confirmation`} cycleNumber={cycle.cycle_number} />
        </div>
      ))}
      <p className="text-xs text-ink-soft">
        {phDeclared ? <Mark ok yes="PH declaration recorded" /> : <span className="font-semibold text-warn">Waiting for the PH declaration</span>}
        {' - '}
        {rsConfirmed ? <Mark ok yes={`${supervisor} confirmation recorded`} /> : <span className="font-semibold text-warn">Waiting for the RS confirmation</span>}
      </p>
    </div>
  )
}

function StopsSection({ cycle, bundle }: { cycle: CycleView; bundle: PermitBundle }) {
  if (cycle.suspensions.length === 0) return null
  return (
    <Section title="Stops and suspension" ruleIds={['CORE-040', 'CORE-062', 'CORE-063']}>
      {cycle.suspensions.map((s) => (
        <StopBlock key={s.id} stop={s} cycle={cycle} bundle={bundle} />
      ))}
    </Section>
  )
}

// -----------------------------------------------------------------------------
// Card and view
// -----------------------------------------------------------------------------
function CycleCard({ bundle, cycle, isCurrent, initiallyOpen, people }: {
  bundle: PermitBundle
  cycle: CycleView
  isCurrent: boolean
  initiallyOpen: boolean
  people: People
}) {
  const [open, setOpen] = useState(initiallyOpen)
  const revision = revisionOf(bundle, cycle)
  const gasPlan = gasPlanOf(revision)
  const predecessor = predecessorOf(bundle, cycle)
  const n = cycle.cycle_number
  return (
    <Card accent={isCurrent ? 'class-b' : undefined}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="display flex flex-wrap items-center gap-2 text-lg font-semibold text-ink">
            Cycle {n}
            <CycleStateBadge state={cycle.cycle_state} />
            {isCurrent ? <Badge tone="brand">Current cycle</Badge> : null}
          </h3>
          <p className="mt-1 text-sm text-ink">
            Requested window: <WindowText from={cycle.requested_from} to={cycle.requested_to} />
          </p>
          <p className="text-xs text-ink-soft">
            Requested by {cycle.requested_by.display_name}, {fmtDateTime(cycle.created_at)}
            {predecessor ? ` - follows cycle ${predecessor.cycle_number}` : ''}
          </p>
          {cycle.ended_at ? (
            <p className="text-xs text-ink-soft">
              Ended {fmtDateTime(cycle.ended_at)}: {END_REASON_TEXT[cycle.end_reason ?? ''] ?? humanise(cycle.end_reason)}
            </p>
          ) : null}
        </div>
        <Button type="button" variant="secondary" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide details' : 'Show details'}
        </Button>
      </div>
      <StopAlert cycle={cycle} isCurrent={isCurrent} />
      <CycleProgress cycle={cycle} gasPlan={gasPlan} />
      {open ? (
        <div className="mt-4 space-y-6">
          <p className="rounded-lg border border-line bg-paper p-2 text-sm text-ink">
            The signatures below belong to cycle {n} only. They do not carry over: every new cycle needs its own pre-issue,
            validation, gas test, toolbox talk and acceptance.
          </p>
          <PreissueSection cycle={cycle} />
          <ValidationSection cycle={cycle} bundle={bundle} people={people} />
          <ClassASection cycle={cycle} bundle={bundle} />
          <ExaminationsSection cycle={cycle} required={requiresFirstDayExam(revision, cycle) || bundle.class_a?.daily_examination === 'required'} />
          <GasSection cycle={cycle} gasPlan={gasPlan} synthetic={bundle.permit.is_synthetic} />
          <TalksSection cycle={cycle} />
          <AcceptanceSection cycle={cycle} />
          <StopsSection cycle={cycle} bundle={bundle} />
        </div>
      ) : null}
    </Card>
  )
}

export function CyclesView({ bundle }: PermitViewProps) {
  const registry = useRegistry()
  const cycles = sortCyclesNewestFirst(bundle.cycles)
  if (cycles.length === 0) {
    return (
      <EmptyState title="No daily cycles yet">
        {bundle.permit.lifecycle_state === 'authorised'
          ? 'The PA, PH or RS requests the first cycle (once the Operations Manager has approved the permit, where the policy requires it). Each cycle then needs an RS pre-issue, an RS validation and PH acceptance before work.'
          : 'Daily cycles start after the RS authorises the permit.'}
      </EmptyState>
    )
  }
  const openId = cycles.some((c) => c.id === bundle.permit.current_cycle_id) ? bundle.permit.current_cycle_id : cycles[0].id
  return (
    <div className="space-y-4">
      <Banner tone="info" title="Each cycle stands on its own">
        Every daily cycle needs its own RS pre-issue, RS validation, gas test, toolbox talk and PH acceptance. Signatures on
        one cycle never count for another. Whether work may proceed is decided by the server: see the status at the top of
        the permit.
        <RuleRefs ruleIds={['CORE-052', 'CORE-055', 'CORE-058']} />
      </Banner>
      {cycles.map((c) => (
        <CycleCard
          key={c.id}
          bundle={bundle}
          cycle={c}
          isCurrent={c.id === bundle.permit.current_cycle_id}
          initiallyOpen={c.id === openId}
          people={registry.data?.people ?? []}
        />
      ))}
    </div>
  )
}
